/**
 * Catálogo de permisos finos del backoffice.
 *
 * Convención CANÓNICA del backend: `<recurso>:<accion>` (singular, dos puntos)
 * — es lo que el seed de identity-access persiste y lo que viaja firmado en el
 * claim `permissions` del JWT. Esta lista es el contrato de UI para
 * ocultar/mostrar acciones por UX; el backend sigue siendo la autoridad.
 *
 * Los datos de salud (EPS) tienen permiso DEDICADO y auditado (Ley 25.326).
 *
 * TODO(catálogo): los permisos marcados [SIN BACKEND] no existen todavía en el
 * catálogo del backend y se mapean al recurso más cercano. Unificar cuando esos
 * contextos salgan de Fase 0 (journey, incidents, admin).
 */
export const PERMISSIONS = {
  // Flota
  FLEET_READ: 'fleet:read',
  FLEET_WRITE: 'fleet:write',
  // Choferes (Anexo E)
  DRIVERS_READ: 'driver:read',
  DRIVERS_WRITE: 'driver:write',
  // Centro documental (motor de vencimientos)
  DOCUMENTS_READ: 'document:read',
  DOCUMENTS_WRITE: 'document:write',
  // Certificar es un permiso DEDICADO (R2-010, separación de funciones): en el
  // seed sólo admin_pampa lo tiene — cargar (write) NO habilita certificar.
  DOCUMENTS_APPROVE: 'document:approve',
  // Rutas y tramos (routing-geo) — trazados del servicio y clase de riesgo. El
  // backend exige route:read en GET /routing/routes (+ /:id/sheet, /:id/deviation).
  ROUTE_READ: 'route:read',
  // Reservas / cupos
  BOOKINGS_READ: 'booking:read',
  BOOKINGS_WRITE: 'booking:write',
  BOOKINGS_OVERRIDE: 'booking:override',
  // Importación masiva de reservas (LION-805) — permiso DEDICADO del back-office:
  // el backend exige booking:import en todos los endpoints de /booking-imports.
  BOOKINGS_IMPORT: 'booking:import',
  // Viajes / live-map / telemetría
  TRIPS_READ: 'trip:read',
  TRIPS_WRITE: 'trip:write', // programar servicio (POST /trips)
  LIVEMAP_READ: 'telemetry:read', // el mapa vive de la telemetría
  TELEMETRY_READ: 'telemetry:read',
  // MDVR (video, acceso auditado) — el backend exige telemetry:read en
  // GET /mdvr/recordings y en GET /mdvr/recordings/:id/media-url (playback).
  MDVR_READ: 'telemetry:read',
  MDVR_DOWNLOAD: 'telemetry:read',
  // Journey nocturno (Anexo F)
  JOURNEY_APPROVE: 'journey:approve', // aprobación de viaje nocturno — permiso DEDICADO (QA-004, separación de funciones): sólo HSE/admin
  JOURNEY_CREATE: 'trip:write', // solicitar journey nocturno (lo hace quien programa viajes)
  // Checklists pre-viaje — tarea OPERATIVA, permiso dedicado (QA-013): lo tiene operador_flota
  CHECKLIST_READ: 'checklist:read',
  // Incidentes / contingencia
  INCIDENTS_READ: 'hse:read', // [SIN BACKEND] incidentes cuelgan de HSE
  INCIDENTS_WRITE: 'hse:write', // [SIN BACKEND]
  // HSE / EPS — datos de salud sensibles, vista segregada + audit
  HSE_READ: 'hse:read',
  EPS_HEALTH_READ: 'eps:read',
  EPS_HEALTH_WRITE: 'eps:write',
  // Disciplina (Anexo G, 3 strikes / 6 meses) — contexto drivers-compliance
  DISCIPLINE_READ: 'discipline:read',
  DISCIPLINE_MANAGE: 'discipline:manage',
  // Document Compliance Gate (LION-744/LION-784) — contexto drivers-compliance,
  // generalizado a driver|vehicle. `exception-grant` y `catalog-manage` son los
  // permisos DEDICADOS que el contrato pineado exige (403 "Caller lacks
  // compliance:exception-grant" / "...compliance:catalog-manage" en
  // contracts/openapi.json). `dashboard-read` gatea la lectura del worklist:
  // el endpoint del contrato sólo exige bearer (sin 403 documentado todavía),
  // así que este permiso es defensivo del lado UI (oculta por UX) hasta que el
  // backend declare su propio 403 — la autoridad real sigue siendo el backend.
  COMPLIANCE_DASHBOARD_READ: 'compliance:dashboard-read',
  COMPLIANCE_EXCEPTION_GRANT: 'compliance:exception-grant',
  COMPLIANCE_CATALOG_MANAGE: 'compliance:catalog-manage',
  // KPIs / reportería
  KPIS_READ: 'reporting:read',
  REPORTS_READ: 'reporting:read',
  REPORTS_EXPORT: 'reporting:read', // [SIN BACKEND] export = read de reportes
  // Facturación / certificación / penalidades
  BILLING_READ: 'billing:read',
  BILLING_MANAGE: 'billing:write',
  PENALTIES_READ: 'billing:read', // superficie de penalidades = read de facturación
  PENALTIES_MANAGE: 'billing:write', // penalidades = billing (POST /penalties-billing/penalties/evaluate)
  // Revisión de penalidades (aplicar/eximir) — permiso DEDICADO del backend:
  // PATCH /penalties-billing/penalties/:id/review exige penalty:review.
  PENALTIES_REVIEW: 'penalty:review',
  // Bandeja de notificaciones in-app (motor de notificaciones) — el backend
  // exige este permiso en GET /notifications (403 sin él). La campana del topbar
  // es visible para todo backoffice logueado, pero sólo consulta la API cuando el
  // usuario lo tiene (evita el toast global de "acceso denegado").
  NOTIFICATIONS_READ: 'notifications:read',
  // Admin del tenant (consola RBAC) — contexto identity-access
  ADMIN_USERS: 'admin:users',
  ADMIN_ROLES: 'admin:roles',
  ADMIN_TENANT: 'admin:tenant', // legacy: sin uso directo tras la consola admin

  // Super-admin PVP (cross-tenant)
  TENANT_SWITCH: 'tenant:switch',
  // Alta y gestión de empresas (tenants) + invitación al primer admin (LION-864).
  // El backend lo exige en /admin/tenants; sólo el JWT del super_admin lo trae.
  ADMIN_TENANTS: 'admin:tenants',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
